import * as R from 'ramda';

import { distance as getGeoDistance, getBearing } from '@utils/geodesy.ts';

import type { LngLat, MapFeature } from './types.ts';

const TIME_BETWEEN_ANIMATED_LINES = 3.75;

type AnimatedPoint = {
  distance: number;
  coordinate: LngLat;
};

export type AnimatedNavLineSection = {
  points: AnimatedPoint[];
  startTime: number;
  endTime: number;
  ordinal: number;
};

type Segment = {
  ordinalId: string;
  coordinates: LngLat[];
};

type AnimatedNavLineFeature = MapFeature & {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: LngLat[];
  };
  properties: {
    category: 'nav.animatedline';
    ordinal: number;
  };
};

export const animatedNavLineFeaturesAtTime = (
  timeOffsetMs: number,
  rawAnimatedNavLineSections: AnimatedNavLineSection[],
  timeBetweenLinesSec = TIME_BETWEEN_ANIMATED_LINES,
): AnimatedNavLineFeature[] => {
  if (rawAnimatedNavLineSections.length === 0) {
    return [];
  }

  const features: AnimatedNavLineFeature[] = [];
  let time = (timeOffsetMs / 1000) % timeBetweenLinesSec;
  const endTime = rawAnimatedNavLineSections[rawAnimatedNavLineSections.length - 1]?.endTime ?? 0;

  while (time < endTime) {
    const section = findSectionAtTime(time, rawAnimatedNavLineSections);
    if (!section) {
      time += timeBetweenLinesSec;
      continue;
    }

    const points = getPolylineForTime(section, time);
    features.push(createNavSectionFeature(points, section.ordinal));
    time += timeBetweenLinesSec;
  }

  return features;
};

const findSectionAtTime = (time: number, rawAnimatedNavLineSections: AnimatedNavLineSection[]) =>
  rawAnimatedNavLineSections.find(({ startTime, endTime }) => startTime <= time && time <= endTime);

const createNavSectionFeature = (points: AnimatedPoint[], ordinal: number): AnimatedNavLineFeature => ({
  type: 'Feature',
  geometry: {
    type: 'LineString',
    coordinates: points.map(point => point.coordinate),
  },
  properties: {
    category: 'nav.animatedline',
    ordinal,
  },
});

const getPolylineForTime = (section: AnimatedNavLineSection, time: number): AnimatedPoint[] => {
  const startTime = section.startTime;
  const endTime = section.endTime;
  const sectionDistance = section.points[section.points.length - 1]?.distance ?? 0;
  const halfTime = (endTime + startTime) / 2;

  if (time < halfTime) {
    const normalizedTime = (time - startTime) / (halfTime - startTime);
    const easeIn = normalizedTime * normalizedTime;
    const distance = Math.max(0.0, easeIn * sectionDistance);
    const endIndex = pointIndexAtDistance(distance, section.points);
    return [...section.points.slice(0, endIndex), interpolatedPointAtIndex(section.points, endIndex, distance)];
  }

  const normalizedTime = 1 - (time - halfTime) / (endTime - halfTime);
  const easeOut = 1 - normalizedTime * normalizedTime;
  const distance = Math.min(sectionDistance, sectionDistance * easeOut);
  const startIndex = pointIndexAtDistance(distance, section.points);
  return [interpolatedPointAtIndex(section.points, startIndex, distance), ...section.points.slice(startIndex)];
};

const interpolatedPointAtIndex = (points: AnimatedPoint[], index: number, distance: number): AnimatedPoint => {
  const startPoint = points[index - 1];
  const endPoint = points[index] ?? points[points.length - 1];

  if (!startPoint || !endPoint || endPoint.distance === startPoint.distance) {
    return points[Math.max(0, Math.min(index, points.length - 1))] ?? { coordinate: [0, 0], distance };
  }

  const t = (distance - startPoint.distance) / (endPoint.distance - startPoint.distance);
  const lng = startPoint.coordinate[0] + t * (endPoint.coordinate[0] - startPoint.coordinate[0]);
  const lat = startPoint.coordinate[1] + t * (endPoint.coordinate[1] - startPoint.coordinate[1]);
  return { coordinate: [lng, lat], distance };
};

const pointIndexAtDistance = (distance: number, points: AnimatedPoint[]) => {
  const index = points.findIndex((point, i) => i > 0 && point.distance >= distance);
  return index >= 0 ? index : Math.max(1, points.length - 1);
};

export const prepareRawAnimatedNavlineSections = (segments: Segment[][]): AnimatedNavLineSection[][] => {
  const sections: AnimatedNavLineSection[][] = [];

  segments.forEach(segmentGroup => {
    let currentFloorOrdinal: number | null = null;
    let time = 0;
    let currentSection: LngLat[] = [];
    const groupSections: AnimatedNavLineSection[] = [];

    const filteredSegmentGroup = segmentGroup.filter(segment => segment.coordinates.length > 1);

    const addNewSection = () => {
      if (currentFloorOrdinal == null || currentSection.length < 2) {
        currentSection = [];
        return;
      }

      const section = initSection(currentFloorOrdinal, currentSection, time);
      time = section.endTime;
      groupSections.push(section);
      currentSection = [];
    };

    filteredSegmentGroup.forEach(segment => {
      const segmentOrdinal = Number(segment.ordinalId.split(': ')[1]);
      if (!R.isNil(currentFloorOrdinal) && segmentOrdinal !== currentFloorOrdinal) {
        addNewSection();
      } else if (currentSection.length && R.last(currentSection) === R.head(segment.coordinates)) {
        addNewSection();
      }

      currentFloorOrdinal = segmentOrdinal;
      segment.coordinates.forEach(coordinate => {
        const lastCoordinate = R.last(currentSection);
        if (currentSection.length && R.equals(lastCoordinate, coordinate)) {
          return;
        }

        if (currentSection.length < 2) {
          currentSection.push(coordinate);
          return;
        }

        if (!lastCoordinate) {
          currentSection.push(coordinate);
          return;
        }

        const beforeLastCoordinate = currentSection[currentSection.length - 2] as LngLat;
        const bearing1 = getBearing(lastCoordinate[1], lastCoordinate[0], coordinate[1], coordinate[0]);
        const bearing2 = getBearing(
          beforeLastCoordinate[1],
          beforeLastCoordinate[0],
          lastCoordinate[1],
          lastCoordinate[0],
        );
        const angle = Math.abs(bearing1 - bearing2);
        if (angle > 45 && angle < 315) {
          addNewSection();
          currentSection.push(lastCoordinate);
        }
        currentSection.push(coordinate);
      });
    });

    if (currentSection.length > 1) {
      addNewSection();
    }

    sections.push(groupSections);
  });

  return sections;
};

const initSection = (ordinal: number, coordinates: LngLat[], startTime: number): AnimatedNavLineSection => {
  let distance = 0;
  const points: AnimatedPoint[] = [];

  for (let i = 0; i < coordinates.length; i++) {
    const prev = i ? coordinates[i - 1] : undefined;
    const coordinate = coordinates[i] as LngLat;
    const distanceToPrev = prev ? getGeoDistance(...coordinate, ...prev) : 0;
    distance += distanceToPrev;
    points.push({ distance, coordinate });
  }

  const time = Math.max(0.5, Math.min(2, Math.sqrt(distance) / 4));
  const endTime = startTime + time;

  return {
    points,
    startTime,
    endTime,
    ordinal,
  };
};
